import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { SessionView } from 'shared-models/browser';
import { sessionsService } from '../../../services/sessions/sessions.service';

export type PendingTable = { tableId: number; tableLabel: string };

type UseOpenSessionFlowParams = {
  tenantId: string;
  employeeSession: string | null;
  setActiveSession: Dispatch<SetStateAction<SessionView | null>>;
  setError: (message: string | null) => void;
  onSessionOpened: (pending: PendingTable) => void;
};

// Summary: Owns open/edit opening-cash modal state + async open/update (keeps PosShell thin).
export function useOpenSessionFlow({
  tenantId,
  employeeSession,
  setActiveSession,
  setError,
  onSessionOpened,
}: UseOpenSessionFlowParams) {
  const [modalMode, setModalMode] = useState<'open' | 'edit' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const pendingTableRef = useRef<PendingTable | null>(null);
  const saveInFlightRef = useRef(false);

  const requestOpenSession = useCallback((pending: PendingTable) => {
    pendingTableRef.current = pending;
    setModalMode('open');
  }, []);

  const requestEditOpeningCash = useCallback(() => {
    pendingTableRef.current = null;
    setModalMode('edit');
  }, []);

  const closeModal = useCallback(() => {
    if (saveInFlightRef.current) return;
    pendingTableRef.current = null;
    setModalMode(null);
  }, []);

  // Summary: Opens with the declared float; continues the pending table only on success.
  const confirmOpen = useCallback(async (openingCash: number) => {
    if (!employeeSession || saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      const opened = await sessionsService.open(tenantId, employeeSession, openingCash);
      setActiveSession(opened);
      const pending = pendingTableRef.current;
      pendingTableRef.current = null;
      setModalMode(null);
      if (pending) {
        onSessionOpened(pending);
      }
    } catch (openError: unknown) {
      setError(openError instanceof Error ? openError.message : 'Failed opening session');
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [employeeSession, tenantId, setActiveSession, setError, onSessionOpened]);

  // Summary: Diff-only edit (Rule 19) — unchanged closes with no request; else PATCH + await SSE.
  const confirmEdit = useCallback(async (openingCash: number, baseline: number) => {
    if (!employeeSession || saveInFlightRef.current) return;
    if (openingCash === baseline) {
      setModalMode(null);
      return;
    }
    saveInFlightRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await sessionsService.updateOpeningCash(tenantId, employeeSession, openingCash);
      setModalMode(null);
    } catch (editError: unknown) {
      setError(editError instanceof Error ? editError.message : 'Failed updating opening cash');
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [employeeSession, tenantId, setError]);

  return {
    modalMode,
    isSaving,
    requestOpenSession,
    requestEditOpeningCash,
    closeModal,
    confirmOpen,
    confirmEdit,
  };
}
